import { useMemo } from 'react';
import { SaveDialog } from '@/components/editor/dialogs/SaveDialog';
import { ImportJsonDialog } from '@/components/editor/dialogs/ImportJsonDialog';
import { RepoUploadDialog } from '@/components/ai/RepoUploadDialog';
import { HelpDialog } from '@/components/editor/dialogs/HelpDialog';
import { AiJsonDialog } from '@/components/ai/AiJsonDialog';
import { AiAssistantDialog } from '@/components/ai/AiAssistantDialog';
import { OverviewDialog } from '@/components/editor/dialogs/OverviewDialog';
import { ExportPngDialog } from '@/components/editor/dialogs/ExportPngDialog';
import { ExportJsonDialog } from '@/components/editor/dialogs/ExportJsonDialog';
import { FeatureRequestDialog } from '@/components/editor/dialogs/FeatureRequestDialog';
import { BugReportDialog } from '@/components/editor/dialogs/BugReportDialog';
import { AuthDialog } from '@/components/editor/dialogs/AuthDialog';
import { WorkspacePickerDialog } from '@/components/editor/dialogs/workspace-picker/WorkspacePickerDialog';
import { ShareWorkspaceDialog } from '@/components/editor/dialogs/ShareWorkspaceDialog';
import { ProcessMotorDialog } from '@/components/workflow/process/ProcessMotorDialog';
import { IS_WEB } from '@/lib/buildMode';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useDialogs } from '@/contexts/DialogContext';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';
import type { Document } from '@/types/document';
import { useAiPersona } from '@/functions/ai/useAiPersona';
import { WORKFLOW_PROFILE_KEY, type WorkflowProfile } from '@/lib/workflowTemplates';

export interface IndexDialogsProps {
  // Save / load / import — wrapped handlers from caller
  currentDocumentId: string | null;
  onSaveToDb: (title: string, category: string, asNew: boolean, isOfficial: boolean) => Promise<void>;
  onList: () => Promise<Document[]>;
  onLoad: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  /** C4: overLimit=true → ladda i preview-mode (inga mutations). */
  onImport: (state: WorkflowState | MultiSheetWorkflowState, mode: 'append' | 'replace', overLimit?: boolean) => void;
  /** Plan-limits för tier-gated JSON import. */
  importLimits?: import('@/lib/planTiers').PlanLimits;
  /**
   * I-001: metadata-argumentet används av AI-vägen för att länka den
   * applicerade proposalen till conversation + intent → skapar en
   * artifact_version-rad vid Apply.
   */
  onStageAiProposal?: (
    state: WorkflowState,
    meta?: { conversationId?: string; intent?: import('@/lib/intentTypes').Intent; promptMessageId?: string },
  ) => void;
  /** AA3: switch till 3D-vy efter multi-sheet-import från AI. */
  onViewIn3D?: () => void;

  /** Legacy fallback. Ignored when the composed persona prompt is available. */
  aiHoningPrompt?: string;

  shareCoopEnabled?: boolean;
  onShareToggleCoop?: (enabled: boolean) => Promise<boolean>;

  onCloudLoad: (state: WorkflowState | MultiSheetWorkflowState, name: string, docId: string) => void;

  onExportView: (format: 'png' | 'svg') => Promise<void>;
  onExportAll: (format: 'png' | 'svg', viewMode?: 'flat' | 'detailed' | 'tree' | 'compact') => Promise<void>;

  /** Snapshot-getter för action-log tail som bifogas rapporter. */
  getActionLogSnapshot?: () => unknown;
  /** Workflow-stats som bifogas bug-rapport. */
  getWorkflowStats?: () => { blocks: number; connectors: number; groups: number };
}

function readWorkflowProfile(): WorkflowProfile | null {
  try {
    const raw = localStorage.getItem(WORKFLOW_PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Single render-site for all page-level dialogs.
 *
 * Previously this component was purely presentational and received `aiHoningPrompt`
 * as a prop. To activate AI-personalization without requiring a 3-line patch to
 * the big Index.tsx, we now read the workflow profile directly from localStorage
 * and compose the full 6-layer system prompt via `useAiPersona`. The legacy
 * `aiHoningPrompt` prop is still accepted as a fallback.
 */
export function IndexDialogs(props: IndexDialogsProps) {
  // Workflow data + state-snapshot getters from context.
  const wf = useWorkflow();
  // Dialog open/close flags from context — replaces 28 dlg props from caller.
  const dlg = useDialogs();

  // Read workflow profile reactively — the memo key includes `aiHoningPrompt`
  // because Index.tsx updates that prop whenever the profile changes, which
  // gives us "re-read on profile change" without needing a prop drill.
  const workflowProfile = useMemo(
    () => readWorkflowProfile(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.aiHoningPrompt],
  );

  const { prompt } = useAiPersona(workflowProfile);
  const effectivePrompt = prompt.full || props.aiHoningPrompt;

  return (
    <>
      <SaveDialog
        open={dlg.saveOpen}
        onOpenChange={(v) => { dlg.setSaveOpen(v); if (!v) dlg.setSaveAsNew(false); }}
        defaultTitle={wf.workflowName}
        existingId={dlg.saveAsNew ? null : props.currentDocumentId}
        onSave={props.onSaveToDb}
      />

      {/* WorkspacePickerDialog ersätter de tidigare Load- och Cloud-dialogerna
       *  (#9 Wave E.2 step 5). NewWorkflowDialog stannar separat
       *  (triggas från sin egen entry-point). Unified open = OR av
       *  båda gamla flags; defaultTab beror på vilken som öppnades.
       *  showNewTab=false så vi inte exponerar New-tab här (den hör
       *  hemma i NewWorkflowDialog som har egen trigger-knapp). */}
      <WorkspacePickerDialog
        open={dlg.loadOpen || dlg.cloudWorkspaceOpen}
        onOpenChange={(v) => {
          if (!v) {
            dlg.setLoadOpen(false);
            dlg.setCloudWorkspaceOpen(false);
          }
        }}
        defaultTab={dlg.cloudWorkspaceOpen ? 'cloud' : 'recent'}
        showNewTab={false}
        onList={props.onList}
        onLoadLocal={props.onLoad}
        onDelete={props.onDelete}
        onOpenRepoUpload={dlg.openRepoUpload}
        currentState={wf.getState}
        currentName={wf.workflowName}
        onLoadCloud={props.onCloudLoad}
      />

      <RepoUploadDialog
        open={dlg.repoUploadOpen}
        onOpenChange={dlg.setRepoUploadOpen}
        onImport={props.onImport}
        limits={props.importLimits}
      />

      <ImportJsonDialog
        open={dlg.importOpen}
        onOpenChange={dlg.setImportOpen}
        onImport={props.onImport}
        limits={props.importLimits}
      />


      <HelpDialog
        open={dlg.helpOpen}
        onOpenChange={dlg.setHelpOpen}
      />

      <AiJsonDialog
        open={dlg.aiJsonOpen}
        onOpenChange={dlg.setAiJsonOpen}
      />

      <AiAssistantDialog
        open={dlg.aiAssistantOpen}
        onOpenChange={dlg.setAiAssistantOpen}
        onImport={(state, overLimit) => props.onImport(state, 'replace', overLimit)}
        onViewIn3D={props.onViewIn3D}
        onStageAiProposal={props.onStageAiProposal}
        aiHoningPrompt={effectivePrompt}
        // Fas 2: skicka FULL multi-sheet-state så AI får alla sheets +
        // cross-sheet-connectors i EXTEND-guard, inte bara active sheet.
        getCurrentWorkflowState={wf.getState}
        // Fas 7: workflow_id behövs för att pre-fetcha entity_details
        // (description_md + attachment-filenames) som berikar snapshoten
        // i extend-mode.
        currentWorkflowId={props.currentDocumentId}
      />

      {IS_WEB && (
        <FeatureRequestDialog
          open={dlg.featureRequestOpen}
          onOpenChange={dlg.setFeatureRequestOpen}
          getSnapshot={props.getActionLogSnapshot}
        />
      )}

      {IS_WEB && (
        <BugReportDialog
          open={dlg.bugReportOpen}
          onOpenChange={dlg.setBugReportOpen}
          getSnapshot={props.getActionLogSnapshot}
          getWorkflowStats={props.getWorkflowStats}
        />
      )}

      <AuthDialog
        open={dlg.loginOpen}
        onOpenChange={dlg.setLoginOpen}
      />


      <ShareWorkspaceDialog
        open={dlg.shareWorkspaceOpen}
        onOpenChange={dlg.setShareWorkspaceOpen}
        workspaceId={props.currentDocumentId}
        workspaceName={wf.workflowName}
        coopEnabled={props.shareCoopEnabled}
        onToggleCoopEnabled={props.onShareToggleCoop}
      />

      <ProcessMotorDialog
        open={dlg.processMotorOpen}
        onOpenChange={dlg.setProcessMotorOpen}
        workflowId={props.currentDocumentId}
        workflowName={wf.workflowName}
      />

      <OverviewDialog
        open={dlg.overviewOpen}
        onOpenChange={dlg.setOverviewOpen}
        blocks={wf.blocks}
        connectors={wf.connectors}
        groups={wf.groups}
      />

      <ExportPngDialog
        open={dlg.exportPngOpen}
        onOpenChange={dlg.setExportPngOpen}
        onExportView={props.onExportView}
        onExportAll={props.onExportAll}
      />

      <ExportJsonDialog
        open={dlg.exportJsonOpen}
        onOpenChange={dlg.setExportJsonOpen}
        getState={wf.getState}
      />
    </>
  );
}
